import { useEffect, useState } from 'react';
import { getWikiPage, WikiPageBundle, WikiCitation } from '../api/wiki';

interface CitationPopoverProps {
  citation: WikiCitation;
  pid: string;
  onClose: () => void;
}

function CitationPopover({ citation, pid, onClose }: CitationPopoverProps) {
  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'fixed',
        top: '20%',
        left: '50%',
        transform: 'translateX(-50%)',
        background: 'var(--bg, #1e1e1e)',
        border: '1px solid var(--border, #555)',
        borderRadius: 8,
        padding: 16,
        maxWidth: 640,
        zIndex: 1000,
        boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <strong>ⓘ Citation</strong>
        <button onClick={onClose}>✕</button>
      </div>
      <div className="muted small" style={{ marginTop: 8 }}>
        Source: {citation.source_table}:{citation.source_id}
      </div>
      {citation.source_unavailable ? (
        <div className="banner banner-warning" style={{ margin: '8px 0' }}>
          Source no longer available
        </div>
      ) : null}
      <blockquote
        style={{
          margin: '12px 0',
          padding: '8px 12px',
          borderLeft: '3px solid var(--accent, #7dd3fc)',
          fontStyle: 'italic',
        }}
      >
        {citation.quote ?? citation.claim_text}
      </blockquote>
      <div style={{ display: 'flex', gap: 8 }}>
        <a
          href={`#/wiki/p/${encodeURIComponent(pid)}/source/${encodeURIComponent(`${citation.source_table}:${citation.source_id}`)}?span=${citation.span_start}-${citation.span_end}`}
          onClick={onClose}
        >
          <button>open source →</button>
        </a>
        <button
          onClick={() => {
            void navigator.clipboard.writeText(
              citation.quote ?? citation.claim_text,
            );
          }}
        >
          copy quote
        </button>
      </div>
    </div>
  );
}

export function PageReader({ pid, slug }: { pid: string; slug: string }) {
  const [bundle, setBundle] = useState<WikiPageBundle | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openCitation, setOpenCitation] = useState<WikiCitation | null>(null);

  useEffect(() => {
    let cancelled = false;
    setBundle(null);
    setError(null);
    void getWikiPage(pid, slug)
      .then((b) => {
        if (!cancelled) setBundle(b);
      })
      .catch((e) => {
        if (!cancelled) setError((e as Error).message);
      });
    return () => {
      cancelled = true;
    };
  }, [pid, slug]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenCitation(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (error)
    return (
      <div style={{ padding: 24 }}>
        <a href={`#/wiki/p/${encodeURIComponent(pid)}`}>← Project</a>
        <div className="banner banner-warning">{error}</div>
      </div>
    );
  if (!bundle) return <div style={{ padding: 24 }}>Loading…</div>;

  // Render the body with [^cN] markers as superscript citation links.
  const body = bundle.page.body ?? '';
  const citationByMarker = new Map(
    bundle.citations.map((c) => [c.marker, c]),
  );

  return (
    <div style={{ padding: 24, maxWidth: 800, margin: '0 auto' }}>
      <a href={`#/wiki/p/${encodeURIComponent(pid)}`}>← Project</a>
      <h1 style={{ marginTop: 8 }}>{bundle.page.title}</h1>
      <div className="muted small" style={{ marginBottom: 24 }}>
        v{bundle.page.version} · {bundle.page.kind} · {bundle.page.slug}
        {bundle.links.inbound_count > 0 ? (
          <>
            {' '}
            ·{' '}
            <a
              href={`#/wiki/p/${encodeURIComponent(pid)}/page/${encodeURIComponent(slug)}/backlinks`}
            >
              {bundle.links.inbound_count} backlinks
            </a>
          </>
        ) : null}
      </div>

      {bundle.page.body_unavailable_reason ? (
        <div className="banner banner-warning">
          Body unavailable: {bundle.page.body_unavailable_reason}
        </div>
      ) : (
        <article
          className="wiki-page-body"
          style={{
            fontFamily: '"Source Serif Pro", Charter, Georgia, serif',
            fontSize: 17,
            lineHeight: 1.6,
            whiteSpace: 'pre-wrap',
          }}
        >
          {renderBodyWithCitations(body, citationByMarker, (cit) =>
            setOpenCitation(cit),
          )}
        </article>
      )}

      {openCitation ? (
        <div
          onClick={() => setOpenCitation(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            zIndex: 999,
          }}
        >
          <CitationPopover
            citation={openCitation}
            pid={pid}
            onClose={() => setOpenCitation(null)}
          />
        </div>
      ) : null}
    </div>
  );
}

function renderBodyWithCitations(
  body: string,
  citationByMarker: Map<string, WikiCitation>,
  onClick: (c: WikiCitation) => void,
): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const re = /\[\^(c\d+)\]/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) !== null) {
    if (m.index > last) parts.push(body.slice(last, m.index));
    const cit = citationByMarker.get(m[1]);
    if (cit) {
      parts.push(
        <sup key={`${m[1]}-${m.index}`}>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              onClick(cit);
            }}
            style={{ color: 'var(--accent, #7dd3fc)' }}
            aria-label={`Citation ${m[1]}`}
          >
            [{m[1].slice(1)}]
          </a>
        </sup>,
      );
    } else {
      parts.push(m[0]);
    }
    last = m.index + m[0].length;
  }
  if (last < body.length) parts.push(body.slice(last));
  return parts;
}
